import {
  Alert,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Form,
  FormField,
  Grid,
  Header,
  Link,
  Multiselect,
  SelectProps,
  SpaceBetween,
  TextContent,
  Toggle,
} from "@amzn/awsui-components-react/polaris"
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces"
import { BaseNavigationDetail } from "@amzn/awsui-components-react/polaris/internal/events"
import accountsApi from "api/accounts"
import { OpportunityRequest } from "api/opportunities"
import { ProfileRequest } from "api/profiles"
import { TaskRequest } from "api/tasks"
import { Analytics } from "aws-amplify"
import { AxiosError } from "axios"
import GeoRegionSelector from "components/GeoRegionSelector"
import PartnerFormField from "components/PartnerFormField"
import { useOpportunityTaskContext } from "context/OpportunityContext"
import { useProfile } from "context/ProfileContext"
import { useWorkstreamCatalog, WorkstreamItemLookup } from "context/WorkstreamCatalogContext"
import { Formik, FormikHelpers, FormikProps } from "formik"
import { Product } from "models/account"
import { OPPORTUNITY_STAGE, VERTICALS_LIST } from "models/opportunity"
import { ProfileData } from "models/profile"
import { OmitWorkstreamId } from "models/shared"
import { WorkstreamItem } from "models/workstream"
import moment from "moment"
import FieldContent from "pages/CreateUpdateTask/FieldContent"
import { DEFAULT_TASK } from "pages/CreateUpdateTask/FormContent"
import ErrorFocus from "pmsa-polaris/components/ErrorFocus"
import Fieldset from "pmsa-polaris/components/Fieldset"
import { DatePickerFormField, InputFormField, SelectFormField, TextAreaFormField } from "pmsa-polaris/components/FormFields"
import LoadingHeader from "pmsa-polaris/components/LoadingHeader"
import NavigationButton from "pmsa-polaris/components/NavigationButton"
import config from "pmsa-polaris/config"
import { useAppContext } from "pmsa-polaris/context/AppContext"
import useFlashbar, { FlashMessage } from "pmsa-polaris/hooks/useFlashbar"
import usePromise from "pmsa-polaris/hooks/usePromise"
import useDebounce from "pmsa-polaris/hooks/useDebounce"
import { toOptions } from "pmsa-polaris/utils"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import routes, { routeParams } from "routes"

import topics from "./tools-topics"
import getValidationSchema from "./validationSchema"

const { sfdcBaseUrl } = config

type TaskFormValues = Omit<OmitWorkstreamId<TaskRequest>, "milestone"> & {
  milestoneId: string
}

export type OpportunityFormValues = OmitWorkstreamId<OpportunityRequest> & {
  accountName: string
  executiveBuyIn?: boolean
  publishingTimeline?: string
  tamPercent?: number
  isIndustrySolution?: boolean
  industrySolutionType?: string
  hasCaseStudies?: boolean
  caseStudyCustomers?: string
  caseStudyCustomerIds?: string[]
  gtmPlan?: string
}

type OpportunityTaskFormValues = {
  opportunity: OpportunityFormValues
  tasks: TaskFormValues[]
}

type ProfileFormValues = {
  profile: ProfileRequest
}

interface WorkstreamOption {
  label: string
  value: string
  description: string
}

interface WorkstreamGroup {
  label?: string
  options: WorkstreamOption[]
}

const DEFAULT_FORM_VALUES = {
  opportunity: {
    accountId: "",
    accountName: "",
    closeDate: moment().add(3, "months").format("YYYY-MM-DD"),
    description: "",
    designWinDetail: "",
    designWinDescription: "* Customer Testimonial <STAR Format> :",
    externalLink: "",
    geo: "",
    goals: "",
    name: "",
    nextStep: "",
    opportunityLineItems: [],
    owner: "",
    region: "",
    stageName: "Qualified" as const,
    title: "",
    workstreamName: "",
    techPriority: "",
    industryWorkload: "",
    strategicPriorities: "",
    awsServices: "",
    executiveBuyIn: false,
    publishingTimeline: "",
    tamPercent: undefined,
    isIndustrySolution: false,
    industrySolutionType: "",
    hasCaseStudies: false,
    caseStudyCustomers: "",
    caseStudyCustomerIds: [],
    gtmPlan: "",
  },
  tasks: [],
}

const WORKSTREAM_TYPE = {
  competency: "Partner Competency",
  ambassador: "AWS Ambassador Program",
} as const

const stageOptions = toOptions(...OPPORTUNITY_STAGE)

const techPriorityOptions = [
  { label: "Data & Analytics", value: "DA" },
  { label: "Migrations and Modernizations", value: "MM" },
  { label: "Agentic AI", value: "AG" },
  { label: "Amazon Connect", value: "AC" },
  { label: "Security", value: "SC" },
  { label: "Industry Solutions", value: "IS" },
  { label: "Incubation (long term)", value: "IL" },
]

const industryWorkloadOptions = [
  { label: "HBA", value: "HB" },
  { label: "Data and Analytics", value: "DA" },
  { label: "Security", value: "SC" },
  { label: "DevOps", value: "DO" },
  { label: "Regulated Industries", value: "RI" },
  { label: "Unregulated Industries", value: "UI" },
  { label: "SAP", value: "SP" },
]

const PARTNER_OFFERING_WORKSTREAM = "Partner Offering"

const INDUSTRY_WORKLOAD_WORKSTREAMS = [
  "Agentic Listing in Marketplace",
  "Integration with Kiro",
  "Quick Suite ISV Adoption",
  "AgentCore Integration",
  "AI Models Recruitment Built on AWS",
  "Multi-Product Solution",
  "Industry Workload Marketplace Listing",
  "Partner Revenue Measurement Adoption",
  "IAM Temporary Access Delegation Adoption",
  "Unified Data Platform",
  "Migration of Industry Workload to AWS",
  "Co-Engineering efforts",
  "AWS European Sovereign Cloud ISV Solution Integration",
  "KSA/GCC Regional Workload Acceleration",
  "MP Listings / API Integrations",
  "Partner Integrations",
  "Customer/Buyer Integrations",
]

const MARKETPLACE_BUYER_WORKSTREAMS = [
  "MP Listings / API Integrations",
  "Partner Integrations",
  "Customer/Buyer Integrations",
]

const WORKSTREAM_TO_STRATEGIC_PRIORITY: Record<string, string> = {
  "Agentic Listing in Marketplace": "Agentic Listing in Marketplace",
  "Integration with Kiro": "Integration with Kiro",
  "Quick Suite ISV Adoption": "Quick Suite ISV Adoption (Index API, Agent2Agent, MCP, 3p Data Providers)",
  "AgentCore Integration": "AgentCore Integration",
  "AI Models Recruitment Built on AWS": "Large Language Model Recruitment Built on AWS [Bedrock through ISVs]",
  "Multi-Product Solution": "Multi-Product Solution",
  "Industry Workload Marketplace Listing": "Industry Workload Marketplace Listing",
  "Partner Revenue Measurement Adoption": "Partner Revenue Measurement Adoption (PRM)",
  "IAM Temporary Access Delegation Adoption": "IAM Temporary Access Delegation Adoption",
  "Unified Data Platform": "Unified Data Platform",
  "Migration of Industry Workload to AWS": "Migration of Industry Workload to AWS",
  "Co-Engineering efforts": "Co-Engineering efforts",
  "AWS European Sovereign Cloud ISV Solution Integration": "AWS European Sovereign Cloud ISV Solution Integration",
  "KSA/GCC Regional Workload Acceleration": "KSA/GCC Regional Workload Acceleration",
}

const PDM_WORKSTREAM_EXECUTIVE_EVENTS = "Senior Executive Events"
const PDM_WORKSTREAM_TRAINING = "Training"
const PDM_WORKSTREAM_GTM = "Go To Market"

const industryOptions = VERTICALS_LIST.map((industry) => ({
  label: industry,
  value: industry,
}))

// Helper function to parse US Consulting PSA data from description
const parseUsConsultingPsaData = (description: string) => {
  const beginMarker = "<!-- BEGIN_US_CONSULTING_PSA_DATA -->"
  const endMarker = "<!-- END_US_CONSULTING_PSA_DATA -->"
  
  const beginIndex = description.indexOf(beginMarker)
  const endIndex = description.indexOf(endMarker)
  
  if (beginIndex !== -1 && endIndex !== -1) {
    const jsonStr = description.substring(beginIndex + beginMarker.length, endIndex).trim()
    try {
      return JSON.parse(jsonStr)
    } catch (e) {
      console.error("Failed to parse US Consulting PSA data:", e)
      return null
    }
  }
  return null
}

// Helper function to remove US Consulting PSA data section from description
const removeUsConsultingPsaData = (description: string) => {
  const beginMarker = "<!-- BEGIN_US_CONSULTING_PSA_DATA -->"
  const endMarker = "<!-- END_US_CONSULTING_PSA_DATA -->"
  
  const beginIndex = description.indexOf(beginMarker)
  const endIndex = description.indexOf(endMarker)
  
  if (beginIndex !== -1 && endIndex !== -1) {
    return description.substring(0, beginIndex) + description.substring(endIndex + endMarker.length)
  }
  return description
}

const TRIGGER_WORKSTREAMS = [PARTNER_OFFERING_WORKSTREAM, ...INDUSTRY_WORKLOAD_WORKSTREAMS]

const toProductMultiSelectOptions = (productList: Product[]) =>
  productList.map((product) => ({
    value: product.productId,
    label: product.name || "Unknown",
    description: `APN Solution ID: ${product.apnOfferingId} - FTR Status: ${product.ftrStatus}`,
  }))

const findWorkstreamByOptionLabel = (workstreamOptions: WorkstreamGroup[], searchLabel: string): WorkstreamGroup | null => {
  // Loop through each top-level group
  for (const group of workstreamOptions) {
    // Check if any option in the current group matches the search label
    const hasMatchingOption = group.options.some((option) => option.label === searchLabel)

    if (hasMatchingOption) {
      return group
    }
  }
  return null
}

const parseCodesFromOpportunityName = (opportunityName: string, workstreamName: string, streamsByName: WorkstreamItemLookup | undefined) => {
  if (!TRIGGER_WORKSTREAMS.includes(workstreamName)) return { techPriority: "", industryWorkload: "", title: "" }

  const parts = opportunityName.split("-")
  if (parts.length < 6) return { techPriority: "", industryWorkload: "", title: "" }

  const techPriorityCodes = techPriorityOptions.map((opt) => opt.value)
  const industryWorkloadCodes = industryWorkloadOptions.map((opt) => opt.value)

  let techPriority = ""
  let industryWorkload = ""
  const titleParts = []

  // Find the position after PTNR and opportunity type code
  const ptnrIndex = parts.findIndex((part) => part === "PTNR")
  if (ptnrIndex === -1 || ptnrIndex + 1 >= parts.length) return { techPriority: "", industryWorkload: "", title: "" }

  // Look for codes after opportunity type code and separate title
  for (let i = ptnrIndex + 2; i < parts.length; i++) {
    const part = parts[i]
    if (techPriorityCodes.includes(part) && workstreamName === PARTNER_OFFERING_WORKSTREAM) {
      techPriority = part
    } else if (industryWorkloadCodes.includes(part) && INDUSTRY_WORKLOAD_WORKSTREAMS.includes(workstreamName)) {
      industryWorkload = part
    } else {
      // This part is title
      titleParts.push(part)
    }
  }

  return { techPriority, industryWorkload, title: titleParts.join("-") }
}

const FormContent: React.FC = () => {
  const { id } = useParams<{
    id?: string
  }>()

  const navigate = useNavigate()

  const { setContext } = useAppContext()

  const { profileData, profileLoading, profileError } = useProfile()
  const { workstreamOptions, workstreamsLoading, streamsByName } = useWorkstreamCatalog()
  const { opportunityList, taskList, createOpportunity, updateOpportunity, getOpportunity, createTask, getOpportunityList } = useOpportunityTaskContext()

  const isLoading = opportunityList.loading || taskList.loading || workstreamsLoading || profileLoading

  const setFlashMessages = useFlashbar()

  const opportunity = id && opportunityList.data ? opportunityList.data.find((opp) => opp.id === id) : undefined
  const [searchParams] = useSearchParams()
  const [marketplaceOffer, setMarketplaceOffer] = useState(false)
  const [marqueeDesignWin, setMarqueeDesignWin] = useState(false)
  const [apnProducts, setApnProducts] = useState([] as SelectProps.Options | undefined)
  const [accountFieldChanged, setAccountFieldChanged] = useState(false)
  const [selectedApnProducts, setSelectedApnProducts] = useState<OptionDefinition[]>([])
  const [forceGetProducts, setForceGetProducts] = useState(false)
  const [workstreamOptionsSeen, setWorkstreamOptionsSeen] = useState(workstreamOptions)
  const [customerOptions, setCustomerOptions] = useState<SelectProps.Options>([])
  const [customerStatus, setCustomerStatus] = useState<"pending" | "loading" | "finished" | "error">("pending")
  const [selectedCustomers, setSelectedCustomers] = useState<OptionDefinition[]>([])

  const [gtmGoal, setGtmGoal] = useState(false)
  const [trainingGoal, setTrainingGoal] = useState(false)
  const [srExecutiveEventsGoal, setSrExecutiveEventsGoal] = useState(false)

  // account products api
  const [{ loading: productsLoading, data: accountProducts, error: productsError }, getProducts] = usePromise(accountsApi.products)

  const debouncedSearchCustomers = useDebounce(accountsApi.searchAllAccounts, 200)

  // set initial values from default or searchParams
  const [initialValues, setInitialValues] = useState<OpportunityTaskFormValues>({
    tasks: DEFAULT_FORM_VALUES.tasks,
    opportunity: {
      ...DEFAULT_FORM_VALUES.opportunity,
      ...Object.fromEntries(searchParams),
    },
  })

  const { accountId: initialAccountId, accountName: initialAccountName } = initialValues.opportunity

  const [initialPartnerOptions, setInitialPartnerOptions] = useState<OptionDefinition[]>(
    (initialAccountId &&
      initialAccountName && [
        {
          label: initialAccountName,
          value: initialAccountId,
        },
      ]) ||
      []
  )

  // set tooltip sidebar when 'info' button is clicked
  const handleInfoClicked = (toolsTopic: keyof typeof topics) => (e: CustomEvent<BaseNavigationDetail>) => {
    e.preventDefault()
    setContext({ toolsOpen: true, toolsTopic })
  }

  useEffect(() => {
    if (workstreamsLoading) return
    if (workstreamOptions) {
      /* FREE FORM CODE */
      /*
      if (profileData?.freeFormNames) {
        const filteredOptions = findWorkstreamByOptionLabel(workstreamOptions as WorkstreamGroup[], DESIGN_WIN_WORKSTREAM_NAME)
        if (filteredOptions) {
          setWorkstreamOptionsSeen([filteredOptions] as WorkstreamGroup[])
        } else {
          setWorkstreamOptionsSeen(workstreamOptions as WorkstreamGroup[])
        }
      } else {
      */
      setWorkstreamOptionsSeen(workstreamOptions as WorkstreamGroup[])
      //}
    }
  }, [workstreamOptions, profileData, workstreamsLoading])

  // effect to set current opportunity
  useEffect(() => {
    if (!id) {
      setInitialValues((prevState) => ({
        opportunity: {
          ...prevState.opportunity,
          geo: profileData?.geo ?? "",
          region: profileData?.region ?? "",
          /* FREE FORM CODE */
          /*workstreamName: profileData?.freeFormNames ? DESIGN_WIN_WORKSTREAM_NAME : prevState.opportunity.workstreamName,
          title: profileData?.freeFormNames ? prevState.opportunity.name : prevState.opportunity.title,*/
          workstreamName: prevState.opportunity.workstreamName,
          title: prevState.opportunity.title,
        },
        tasks: [],
      }))
    }
  }, [id, profileData, getOpportunity])

  // effect to update initial values from current opportunity
  useEffect(() => {
    if (isLoading) return
    if (id && !opportunity) {
      getOpportunityList()
    }

    if (opportunity) {
      setInitialPartnerOptions([
        {
          label: opportunity.accountName,
          value: opportunity.accountId,
        },
      ])

      opportunity.workstreamName === PARTNER_OFFERING_WORKSTREAM && setForceGetProducts(true)

      opportunity.designWinDetail === "Marketplace Listing" && setMarketplaceOffer(true)
      opportunity.goals === "Marquee Design Win" && setMarqueeDesignWin(true)

      /* FREE FORM CODE */
      /*const extraOppProps = profileData?.freeFormNames
        ? {
            workstreamName: DESIGN_WIN_WORKSTREAM_NAME,
            goals: opportunity.goals ?? "",
            title: opportunity.name,
          }
        : */

      const pdmDesignWinDetailMap: Record<string, string> = {
        [PDM_WORKSTREAM_EXECUTIVE_EVENTS]: "Sr. Executive Event",
        [PDM_WORKSTREAM_TRAINING]:         "Practice Development",
      }

      const marketplaceBuyerDetailMap: Record<string, string> = {
        "MP Listings / API Integrations": "Marketplace Listing",
        "Partner Integrations":           "New Service Integration",
        "Customer/Buyer Integrations":    "Operational Improvement",
      }

      const extraOppProps = {
        workstreamName: opportunity.workstreamName ?? "",
        geo: opportunity.geo ?? profileData?.geo ?? "",
        region: opportunity.region ?? profileData?.region ?? "",
        goals:
          opportunity.workstreamName === PDM_WORKSTREAM_GTM
            ? "GTM"
            : opportunity.workstreamName && TRIGGER_WORKSTREAMS.includes(opportunity.workstreamName) && (!opportunity.goals || opportunity.goals === "")
            ? "Design Win"
            : opportunity.goals ?? "",
        designWinDetail:
          opportunity.workstreamName && pdmDesignWinDetailMap[opportunity.workstreamName]
            ? pdmDesignWinDetailMap[opportunity.workstreamName]
            : opportunity.workstreamName && marketplaceBuyerDetailMap[opportunity.workstreamName]
            ? marketplaceBuyerDetailMap[opportunity.workstreamName]
            : opportunity.designWinDetail ?? "",
      }

      // Parse tech priority and industry workload from opportunity name on update
      const {
        techPriority,
        industryWorkload,
        title: parsedTitle,
      } = parseCodesFromOpportunityName(opportunity.name, opportunity.workstreamName ?? "", streamsByName)

      // Parse US Consulting PSA data from description
      const parsedPsaData = parseUsConsultingPsaData(opportunity.description || "")
      
      // If we have parsed data, populate the customer multiselect
      if (parsedPsaData?.caseStudyCustomerIds && parsedPsaData?.caseStudyCustomers) {
        const customerNames = parsedPsaData.caseStudyCustomers.split("; ")
        const customerIds = parsedPsaData.caseStudyCustomerIds
        const customers = customerIds.map((id: string, index: number) => ({
          value: id,
          label: customerNames[index] || id,
        }))
        setSelectedCustomers(customers)
      }

      setInitialValues({
        opportunity: {
          ...opportunity,
          description: removeUsConsultingPsaData(opportunity.description || "").trim(),
          ...extraOppProps,
          title: parsedTitle || opportunity.title,
          techPriority: opportunity.techPriority || techPriority,
          industryWorkload: opportunity.industryWorkload || industryWorkload,
          // Populate US Consulting PSA fields from parsed data (but not the usConsultingPsa flag itself)
          executiveBuyIn: parsedPsaData?.executiveBuyIn ?? opportunity.executiveBuyIn ?? false,
          publishingTimeline: parsedPsaData?.publishingTimeline ?? opportunity.publishingTimeline ?? "",
          tamPercent: parsedPsaData?.tamPercent ?? opportunity.tamPercent ?? undefined,
          isIndustrySolution: parsedPsaData?.isIndustrySolution ?? opportunity.isIndustrySolution ?? false,
          industrySolutionType: parsedPsaData?.industrySolutionType ?? opportunity.industrySolutionType ?? "",
          hasCaseStudies: parsedPsaData?.hasCaseStudies ?? opportunity.hasCaseStudies ?? false,
          caseStudyCustomers: parsedPsaData?.caseStudyCustomers ?? opportunity.caseStudyCustomers ?? "",
          caseStudyCustomerIds: parsedPsaData?.caseStudyCustomerIds ?? opportunity.caseStudyCustomerIds ?? [],
          gtmPlan: parsedPsaData?.gtmPlan ?? opportunity.gtmPlan ?? "",
        },
        tasks: [],
      })
    }
  }, [opportunity, profileData, getOpportunityList, id, isLoading])

  const handleSubmit = useCallback(
    async (
      { opportunity: { workstreamName, ...opportunity }, tasks }: OpportunityTaskFormValues,
      { setSubmitting }: FormikHelpers<OpportunityTaskFormValues>
    ) => {
      try {
        if (opportunity) {
          const { geo, region } = opportunity

          const parsedWorkstreamId =
            streamsByName && streamsByName.workstreams[workstreamName] && streamsByName.workstreams[workstreamName].id.split(">")[1]
              ? streamsByName.workstreams[workstreamName].id.split(">")[0]
              : streamsByName?.workstreams[workstreamName].id

          const oppLineItems = selectedApnProducts ? selectedApnProducts.map((product) => product.value || "") : []

          let finalDescription = opportunity.description
          if (TRIGGER_WORKSTREAMS.includes(workstreamName) && opportunity.designWinDescription && opportunity.designWinDescription.trim()) {
            finalDescription = `${opportunity.description}\n\n${opportunity.designWinDescription}`
          }

          // Remove any existing US Consulting PSA data section
          finalDescription = removeUsConsultingPsaData(finalDescription).trim()

          // If US Consulting PSA is enabled in profile, append structured data
          if (profileData?.usConsultingPsa && (workstreamName === "Partner Offering" || workstreamName === "[Design Win] Partner Offering")) {
            const psaData = {
              usConsultingPsa: true,
              executiveBuyIn: opportunity.executiveBuyIn,
              publishingTimeline: opportunity.publishingTimeline,
              tamPercent: opportunity.tamPercent,
              isIndustrySolution: opportunity.isIndustrySolution,
              industrySolutionType: opportunity.industrySolutionType,
              hasCaseStudies: opportunity.hasCaseStudies,
              caseStudyCustomers: opportunity.caseStudyCustomers,
              caseStudyCustomerIds: opportunity.caseStudyCustomerIds,
              gtmPlan: opportunity.gtmPlan,
            }
            
            const psaDataJson = JSON.stringify(psaData, null, 2)
            finalDescription = `${finalDescription}\n\n<!-- BEGIN_US_CONSULTING_PSA_DATA -->\n${psaDataJson}\n<!-- END_US_CONSULTING_PSA_DATA -->`
          }

          const opportunityRequest: OpportunityRequest = {
            ...opportunity,
            description: finalDescription,
            workstreamId: parsedWorkstreamId ?? "",
            opportunityLineItems: oppLineItems,
            strategicPriorities: WORKSTREAM_TO_STRATEGIC_PRIORITY[workstreamName] || opportunity.strategicPriorities || "",
            geo,
            region,
          }

          if (id) {
            await getOpportunity((await updateOpportunity(id, opportunityRequest)).id)
            setFlashMessages([
              {
                content: "Opportunity updated successfully",
                presist: true,
                type: "success",
              },
            ])

            navigate(routeParams.opportunitiesDetails({ id }))
          } else {
            const newOpportunity = await getOpportunity((await createOpportunity(opportunityRequest)).id)

            const flashErrors: FlashMessage[] = []

            await Promise.all(
              tasks.map(async ({ workstreamName, ...task }, index) => {
                const taskRequest: TaskRequest = {
                  ...task,
                  geo,
                  region,
                  milestone: {
                    id: "100",
                    name: "Execute",
                  },
                  relatedId: newOpportunity.id,
                }
                try {
                  await createTask(taskRequest)
                } catch (error) {
                  console.error(error)
                  flashErrors.push({
                    content: "There was an issue with creating task.",
                    presist: true,
                    type: "error",
                  })
                }
              })
            )

            setFlashMessages([
              {
                presist: true,
                type: "success",
                content: "Opportunity created successfully",
              },
              ...flashErrors,
            ])

            navigate(routeParams.opportunitiesDetails({ id: newOpportunity.id }))
          }
        }
      } catch (error) {
        const err = error as AxiosError<{ statusCode: number; message: string }>
        if (err.response && err.response.data && err.response.data.statusCode == 403 && err.response.data.message == "user does not own record") {
          sfdcBaseUrl

          const errmsg = `Only records that you own can be updated.  If you need to change the opportunity owner, navigate to ${sfdcBaseUrl}/${id}`
          setFlashMessages([
            {
              type: "error",
              content: errmsg,
            },
          ])
        } else {
          setFlashMessages([
            {
              type: "error",
              content: "There was an issue with creating opportunity.",
            },
          ])
        }
      } finally {
        setSubmitting(false)
        const event = Analytics.record({ name: "submitted opportunity" })
      }
    },
    [streamsByName, selectedApnProducts, id, updateOpportunity, getOpportunity, setFlashMessages, navigate, createOpportunity, createTask, profileData]
  )

  const [prevWorkstream, setPrevWorkstream] = useState("")

  /* FREE FORM CODE */
  /*
  const validationSchema = useMemo(
    () => getValidationSchema(profileData?.freeFormNames ??  false),
    [
      profileData?.freeFormNames
    ]
  )
    */
  const validationSchema = useMemo(() => getValidationSchema(profileData?.usConsultingPsa ?? false), [profileData?.usConsultingPsa])

  const pageTitle = id ? "Update Opportunity" : "Create Opportunity"

  const formikRef = useRef<FormikProps<OpportunityTaskFormValues>>(null)

  useEffect(() => {
    if (
      formikRef.current &&
      formikRef.current.values.opportunity.accountId &&
      TRIGGER_WORKSTREAMS.includes(formikRef.current.values.opportunity.workstreamName) &&
      !productsError
    ) {
      if ((accountFieldChanged || !accountProducts) && !productsLoading && !productsError) {
        getProducts(formikRef.current.values.opportunity.accountId)
        setAccountFieldChanged(false)
      }
    } else if (opportunity) {
      if (((opportunity.opportunityLineItems && opportunity.opportunityLineItems.length > 0) || forceGetProducts) && !productsError) {
        !accountProducts && !productsLoading && getProducts(opportunity.accountId)
      }
    } else {
      setApnProducts([])
    }
  }, [prevWorkstream, accountFieldChanged, getProducts, opportunity, accountProducts, productsLoading, forceGetProducts, productsError])

  useEffect(() => {
    if (productsError || productsLoading) {
      return
    }

    if (accountProducts) {
      if (opportunity && opportunity.opportunityLineItems && opportunity.opportunityLineItems.length > 0) {
        const multiSelect = toProductMultiSelectOptions(accountProducts)
        setApnProducts(multiSelect)
        setSelectedApnProducts(multiSelect.filter((product) => opportunity!.opportunityLineItems!.includes(product.value)))
      } else {
        setApnProducts(toProductMultiSelectOptions(accountProducts))
      }
    }
  }, [productsLoading, productsError, accountProducts, opportunity])

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, errors, values: { tasks, opportunity: oppFormValues }, isSubmitting, setFieldValue }: FormikProps<OpportunityTaskFormValues>) => {
        const handleAddTaskClick = () => {
          setFieldValue("tasks", [...tasks, { ...DEFAULT_TASK, relatedId: "n/a" }])
        }

        const handleTaskDelete = (index: number) => () => {
          const newTasks = [...tasks]
          newTasks.splice(index, 1)

          setFieldValue("tasks", newTasks)
        }

        const selectedOpportunityWorkstream: WorkstreamItem | undefined = streamsByName?.workstreams[oppFormValues.workstreamName]

        const titleOptions = streamsByName?.workstreams[oppFormValues.workstreamName]?.titleOptions?.map((v) => ({
          value: v,
        }))

        const getOpportunityName = (
          profileData: Readonly<ProfileData> | undefined,
          loading: boolean,
          oppFormValues: OpportunityFormValues,
          streamsByName: WorkstreamItemLookup | undefined
        ): string => {
          if (loading) {
            return "-"
          }

          const opportunityTypeCode =
            streamsByName && oppFormValues.workstreamName
              ? streamsByName.workstreams[oppFormValues.workstreamName].id.split(">")[0].split("+")[0]
              : "[Opportunity Type]"

          const nameParts = [
            oppFormValues.geo || "[Geo]",
            oppFormValues.region || "[Region]",
            getFiscalYearString(oppFormValues.closeDate) || "[FY--]",
            "PTNR",
            opportunityTypeCode,
          ]

          // Add tech priority code if selected
          if (oppFormValues.techPriority) {
            nameParts.push(oppFormValues.techPriority)
          }

          // Add industry workload code if selected
          if (oppFormValues.industryWorkload) {
            nameParts.push(oppFormValues.industryWorkload)
          }

          nameParts.push(oppFormValues.title || "[Title]")

          oppFormValues.name = nameParts.join("-")

          return nameParts.join("-")
        }

        const getFiscalYear = (closeDateString: string) => parseInt(new Date(closeDateString).getFullYear().toString().substring(2))

        const getFiscalYearString = (closeDateString: string) => `FY${getFiscalYear(closeDateString)}`

        const existingTitle =
          streamsByName && oppFormValues.workstreamName ? streamsByName.workstreams[oppFormValues.workstreamName].id.split(">")[1] : undefined

        oppFormValues.title = existingTitle || oppFormValues.title

        const opportunityName = getOpportunityName(profileData, opportunityList.loading, oppFormValues, streamsByName)

        const workstreamChanged = (e: any) => {
          if (
            prevWorkstream === "Service Delivery Program" ||
            prevWorkstream === "Partner Competency" ||
            prevWorkstream === "AWS Ambassador Program" ||
            (streamsByName && oppFormValues.workstreamName && streamsByName.workstreams[oppFormValues.workstreamName].id.split(">")[1])
          ) {
            setFieldValue("opportunity.title", "")
            setFieldValue("opportunity.name", "")
          }

          setFieldValue("opportunity.title", "")
          setFieldValue("opportunity.name", "")

          // Update workstreamName with new selection
          setFieldValue("opportunity.workstreamName", e.detail.selectedOption.value)
          // Clear conditional fields when workstream changes
          setFieldValue("opportunity.techPriority", "")
          setFieldValue("opportunity.industryWorkload", "")
          setFieldValue("opportunity.strategicPriorities", WORKSTREAM_TO_STRATEGIC_PRIORITY[e.detail.selectedOption.value] || "")

          setPrevWorkstream(e.detail.selectedOption.value)
          marketplaceOfferChange(false)
          marqueeDesignWinChange(false, e.detail.selectedOption.value)

          // For PDM Goal Workstreams
          const PDM_WORKSTREAMS = [PDM_WORKSTREAM_GTM, PDM_WORKSTREAM_EXECUTIVE_EVENTS, PDM_WORKSTREAM_TRAINING]
          if (PDM_WORKSTREAMS.includes(e.detail.selectedOption.value)) {
            pdmGoalChange(e.detail.selectedOption.value)
          }

          // For Marketplace Buyer Workstreams - auto-set designWinDetail
          const MARKETPLACE_BUYER_DETAIL_MAP: Record<string, string> = {
            "MP Listings / API Integrations": "Marketplace Listing",
            "Partner Integrations": "New Service Integration",
            "Customer/Buyer Integrations": "Operational Improvement",
          }
          if (MARKETPLACE_BUYER_DETAIL_MAP[e.detail.selectedOption.value]) {
            setFieldValue("opportunity.designWinDetail", MARKETPLACE_BUYER_DETAIL_MAP[e.detail.selectedOption.value])
          }

        }

        // For PDM Goal Workstreams
        const pdmGoalChange = (workstream?: string) => {
          setGtmGoal(workstream === PDM_WORKSTREAM_GTM)
          setSrExecutiveEventsGoal(workstream === PDM_WORKSTREAM_EXECUTIVE_EVENTS)
          setTrainingGoal(workstream === PDM_WORKSTREAM_TRAINING)

          const pdmFieldMap: Record<string, { goals?: string; designWinDetail?: string }> = {
            [PDM_WORKSTREAM_GTM]:              { goals: "GTM" },
            [PDM_WORKSTREAM_EXECUTIVE_EVENTS]: { designWinDetail: "Sr. Executive Event" },
            [PDM_WORKSTREAM_TRAINING]:         { designWinDetail: "Practice Development" },
          }

          const fields = workstream && pdmFieldMap[workstream]
          setFieldValue("opportunity.goals",           fields && fields.goals          ? fields.goals          : "")
          setFieldValue("opportunity.designWinDetail", fields && fields.designWinDetail ? fields.designWinDetail : "")
        }

        const marketplaceOfferChange = (e: boolean) => {
          setMarketplaceOffer(e)
          e ? setFieldValue("opportunity.designWinDetail", "Marketplace Listing") : setFieldValue("opportunity.designWinDetail", "")
        }

        const marqueeDesignWinChange = (e: boolean, workstream?: string) => {
          setMarqueeDesignWin(e)

          if (workstream && !TRIGGER_WORKSTREAMS.includes(workstream)) {
            setFieldValue("opportunity.goals", "")
          } else {
            e ? setFieldValue("opportunity.goals", "Marquee Design Win") : setFieldValue("opportunity.goals", "Design Win")
          }
        }

        const apnProductsChange = (e: any) => {
          setSelectedApnProducts(e.detail.selectedOptions)
          setFieldValue(
            "opportunity.opportunityLineItems",
            e.detail.selectedOptions.map((product: any) => product.value)
          )
        }

        const titleChange = (e: string) => {
          setFieldValue("opportunity.title", e)
        }

        const techPriorityChange = (e: any) => {
          setFieldValue("opportunity.techPriority", e.detail.selectedOption.value)
        }

        const industryWorkloadChange = (e: any) => {
          setFieldValue("opportunity.industryWorkload", e.detail.selectedOption.value)
        }

        return (
          <form onSubmit={handleSubmit} className={isLoading ? "loading" : undefined}>
            <Form
              header={<LoadingHeader loading={isLoading}>{pageTitle}</LoadingHeader>}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <NavigationButton variant="link" href={(id && routeParams.opportunitiesDetails({ id })) || routes.home} disabled={isSubmitting || isLoading}>
                    Cancel
                  </NavigationButton>
                  <Button variant="primary" formAction="submit" disabled={isSubmitting || isLoading} loading={isSubmitting || isLoading}>
                    {pageTitle}
                  </Button>
                </SpaceBetween>
              }
              errorText={
                opportunityList.error
                  ? "We can't process the request right now because of an issue with the server. Try again later.  If the problem persists, contact support"
                  : ""
              }
            >
              <ErrorFocus />
              {errors.opportunity?.opportunityLineItems && (
                <Alert statusIconAriaLabel="Error" type="error" header="Validation Error">
                  Add at least one solution if the opportunity is in Complete stage. Add a product or switch to a Marketplace offer.
                </Alert>
              )}
              <SpaceBetween size="l">
                {/* FREE FORM CODE */
                /*!profileData?.freeFormNames && (*/}
                <Container>
                  <FormField label="Opportunity Name" description="Formatted opportunity name based selections">
                    <TextContent>
                      <strong>{opportunityName}</strong>
                    </TextContent>
                  </FormField>
                </Container>
                {/*})*/}
                <ExpandableSection defaultExpanded={true} variant="container" headerText="Opportunity">
                  <SpaceBetween size="l">
                    <SelectFormField
                      name="opportunity.workstreamName"
                      label="Opportunity Type"
                      filteringType="auto"
                      options={workstreamOptionsSeen}
                      onChange={workstreamChanged}
                      required
                      info={
                        <Link variant="info" onFollow={handleInfoClicked("helpOpportunityType")}>
                          Info
                        </Link>
                      }
                      description="Choose the appropriate Workstream from the Catalog"
                    />

                    <PartnerFormField
                      label="Account Name (Partner)"
                      initialOptions={initialPartnerOptions}
                      name="opportunity.accountId"
                      onChange={(e) => {
                        setAccountFieldChanged(true)
                      }}
                      description="Enter the Partner Name associated with the workstream or enter their SPMS ID. Exact match search if within double quotes."
                      required
                    />

                    {(titleOptions && titleOptions?.length > 0 && (
                      <SelectFormField
                        filteringType="auto"
                        label={selectedOpportunityWorkstream?.name === WORKSTREAM_TYPE.competency ? "Competency" : "Service Delivery"}
                        name="opportunity.title"
                        options={titleOptions}
                        description={
                          selectedOpportunityWorkstream?.name === WORKSTREAM_TYPE.competency
                            ? "Choose the appropriate APN Competency title"
                            : "Choose the appropriate APN Service Delivery Program title"
                        }
                        required
                        /*
                        onChange={(e) => {
                          setFieldValue(
                            "opportunity.name",
                            getOpportunityName(profileData, opportunityList.loading, { ...oppFormValues, title: e.detail.selectedOption.value }, streamsByName)
                          )
                        }}
                        */
                      />
                    )) || (
                      <InputFormField
                        key={selectedOpportunityWorkstream?.name || "opptitlekey"}
                        name="opportunity.title"
                        inputMode="text"
                        label={selectedOpportunityWorkstream?.name === WORKSTREAM_TYPE.ambassador ? "Ambassador Full Name" : "Title"}
                        description={
                          selectedOpportunityWorkstream?.name === WORKSTREAM_TYPE.ambassador
                            ? "Enter the full name of the Ambassador"
                            : "Enter the title of the opportunity."
                        }
                        required
                        disabled={selectedOpportunityWorkstream?.id.includes(">")}
                        onChange={(e) => {
                          titleChange(e.detail.value)
                        }}
                      />
                    )}

                    {/* FREE FORM CODE */
                    /*!profileData?.freeFormNames && <GeoRegionSelector namePrefix="opportunity" />*/}
                    <GeoRegionSelector namePrefix="opportunity" />

                    <Grid gridDefinition={[{ colspan: 8 }, { colspan: 8 }]}>
                      <TextAreaFormField
                        label="Description"
                        stretch
                        name="opportunity.description"
                        description="Describe details of the outcome intended for this opportunity."
                        rows={8}
                        required
                      />
                    </Grid>

                    {!id && TRIGGER_WORKSTREAMS.includes(oppFormValues.workstreamName) && (
                      <Grid gridDefinition={[{ colspan: 8 }, { colspan: 8 }]}>
                        <TextAreaFormField
                          name="opportunity.designWinDescription"
                          stretch
                          label="Design Win Description"
                          description="Provide design win description details."
                          rows={8}
                          required
                        />
                      </Grid>
                    )}

                    {TRIGGER_WORKSTREAMS.includes(oppFormValues.workstreamName) && (
                      <Grid gridDefinition={[{ colspan: 8 }, { colspan: 8 }]}>
                        <TextAreaFormField
                          name="opportunity.awsServices"
                          stretch
                          label="AWS Services"
                          description="List the AWS services used (max 500 characters)"
                          rows={4}
                          required
                        />
                      </Grid>
                    )}

                    {oppFormValues.workstreamName === "Partner Offering" && (
                      <SelectFormField
                        label="Tech Priority"
                        name="opportunity.techPriority"
                        options={techPriorityOptions}
                        description="Select the technical priority for this opportunity"
                        onChange={techPriorityChange}
                        required
                      />
                    )}

                    {INDUSTRY_WORKLOAD_WORKSTREAMS.includes(oppFormValues.workstreamName) && !MARKETPLACE_BUYER_WORKSTREAMS.includes(oppFormValues.workstreamName) && (
                      <SelectFormField
                        label="Industry Workload"
                        name="opportunity.industryWorkload"
                        options={industryWorkloadOptions}
                        description="Select the industry workload for this opportunity"
                        onChange={industryWorkloadChange}
                        required
                      />
                    )}

                    {profileData?.usConsultingPsa &&
                      (oppFormValues.workstreamName === "Partner Offering" || 
                       oppFormValues.workstreamName === "[Design Win] Partner Offering") && (
                        <>
                          <FormField label={<span>Does the solution have executive buy in? <span style={{ color: '#d91515' }}>*</span></span>}>
                            <Toggle
                              checked={oppFormValues.executiveBuyIn || false}
                              onChange={(e) => {
                                setFieldValue("opportunity.executiveBuyIn", e.detail.checked)
                              }}
                            >
                              Does the solution have executive buy in?
                            </Toggle>
                          </FormField>

                          <DatePickerFormField
                            label="What is the expected timeline for publishing the solution?"
                            openCalendarAriaLabel={(selectedDate) => "Choose Date" + (selectedDate ? `, selected date is ${selectedDate}` : "")}
                            nextMonthAriaLabel="Next month"
                            placeholder="YYYY/MM/DD"
                            previousMonthAriaLabel="Previous month"
                            todayAriaLabel="Today"
                            name="opportunity.publishingTimeline"
                            description="Select the expected timeline"
                            required
                          />

                          <InputFormField
                            name="opportunity.tamPercent"
                            inputMode="numeric"
                            type="number"
                            label="What percent of total addressable market (TAM) are they targeting?"
                            description="Enter a number between 0-100"
                            required
                          />

                          <FormField label={<span>Is this an industry solution? <span style={{ color: '#d91515' }}>*</span></span>}>
                            <Toggle
                              checked={oppFormValues.isIndustrySolution || false}
                              onChange={(e) => {
                                setFieldValue("opportunity.isIndustrySolution", e.detail.checked)
                                if (!e.detail.checked) {
                                  setFieldValue("opportunity.industrySolutionType", "")
                                  setFieldValue("opportunity.hasCaseStudies", false)
                                  setFieldValue("opportunity.caseStudyCustomers", "")
                                }
                              }}
                            >
                              Is this an industry solution?
                            </Toggle>
                          </FormField>

                          {oppFormValues.isIndustrySolution && (
                            <>
                              <SelectFormField
                                name="opportunity.industrySolutionType"
                                label="What industry?"
                                description="Select the industry"
                                options={industryOptions}
                                filteringType="auto"
                                required
                              />

                              <FormField label={<span>Do they have case studies in this industry? <span style={{ color: '#d91515' }}>*</span></span>}>
                                <Toggle
                                  checked={oppFormValues.hasCaseStudies || false}
                                  onChange={(e) => {
                                    setFieldValue("opportunity.hasCaseStudies", e.detail.checked)
                                    if (!e.detail.checked) {
                                      setFieldValue("opportunity.caseStudyCustomers", "")
                                    }
                                  }}
                                >
                                  Do they have case studies in this industry?
                                </Toggle>
                              </FormField>

                              {oppFormValues.hasCaseStudies && (
                                <>
                                  <SpaceBetween size="xxs">
                                    <TextContent>
                                      <strong>What customers? <span style={{ color: '#d91515' }}>*</span></strong>
                                    </TextContent>
                                    <Multiselect
                                      placeholder="Search and select customers"
                                      options={customerOptions}
                                      onChange={(e) => {
                                        setSelectedCustomers([...e.detail.selectedOptions])
                                        const customerNames = e.detail.selectedOptions.map((c) => c.label).join("; ")
                                        const customerIds = e.detail.selectedOptions.map((c) => c.value || "")
                                        setFieldValue("opportunity.caseStudyCustomers", customerNames)
                                        setFieldValue("opportunity.caseStudyCustomerIds", customerIds)
                                      }}
                                      onLoadItems={async ({ detail: { filteringText } }) => {
                                        if (filteringText && filteringText.length > 2) {
                                          try {
                                            setCustomerStatus("loading")
                                            const accounts = await debouncedSearchCustomers(filteringText)
                                            const options: SelectProps.Option[] = accounts.map((v) => ({
                                              value: v.id,
                                              label: v.name,
                                              description: v.partnerTier || undefined,
                                              labelTag: v.spmsId || undefined,
                                            }))
                                            setCustomerOptions(options)
                                            setCustomerStatus("finished")
                                          } catch (e) {
                                            console.error(e)
                                            setCustomerStatus("error")
                                          }
                                        }
                                      }}
                                      deselectAriaLabel={(e) => `Remove ${e.label}`}
                                      selectedOptions={selectedCustomers}
                                      statusType={customerStatus}
                                      filteringType="manual"
                                      empty="Type at least 3 characters to search for customers"
                                    />
                                  </SpaceBetween>
                                </>
                              )}
                            </>
                          )}

                          <TextAreaFormField
                            name="opportunity.gtmPlan"
                            label="What is the GTM plan for this solution?"
                            description="Describe the go-to-market plan"
                            rows={4}
                            required
                          />
                        </>
                      )}
                  </SpaceBetween>
                </ExpandableSection>
                {TRIGGER_WORKSTREAMS.includes(oppFormValues.workstreamName) && (
                  <ExpandableSection defaultExpanded={true} variant="container" headerText="Solutions">
                    <SpaceBetween size="m">
                      <Toggle
                        checked={marqueeDesignWin}
                        onChange={(e) => {
                          marqueeDesignWinChange(e.detail.checked)
                        }}
                        description="If off, we default to Design Win"
                      >
                        Marquee Design Win
                      </Toggle>

                      <Toggle
                        checked={marketplaceOffer}
                        onChange={(e) => {
                          marketplaceOfferChange(e.detail.checked)
                        }}
                        description="If on, enter Marketplace URL to External Link field"
                      >
                        Marketplace Offer
                      </Toggle>

                      <SpaceBetween size="xxs">
                        <TextContent>
                          <strong>APN Solution Picker:</strong>
                        </TextContent>
                        <Multiselect
                          placeholder="Choose solutions to add as sfdc products"
                          options={apnProducts}
                          onChange={apnProductsChange}
                          deselectAriaLabel={(e) => `Remove ${e.label}`}
                          selectedOptions={selectedApnProducts}
                          disabled={productsLoading || productsError}
                          filteringType="auto"
                          empty={
                            accountProducts !== undefined
                              ? "This partner has no APN Solutions to add - ensure partner created the solution in Partner Central"
                              : "Choose an account to see available products"
                          }
                        />
                      </SpaceBetween>
                    </SpaceBetween>
                  </ExpandableSection>
                )}
                <ExpandableSection defaultExpanded={true} variant="container" headerText="Additional Details">
                  <SpaceBetween size="l">
                    <ColumnLayout columns={3}>
                      <DatePickerFormField
                        label="Close Date"
                        openCalendarAriaLabel={(selectedDate) => "Choose Date" + (selectedDate ? `, selected date is ${selectedDate}` : "")}
                        nextMonthAriaLabel="Next month"
                        placeholder="YYYY/MM/DD"
                        previousMonthAriaLabel="Previous month"
                        todayAriaLabel="Today"
                        name="opportunity.closeDate"
                        description="The tentative date of when the opportunity will be closed."
                        required
                      />

                      <SelectFormField
                        label="Stage"
                        name="opportunity.stageName"
                        options={stageOptions}
                        description="Adjust the stage through opportunity progression.  Must be Completed or Closed Lost with description when close date is reached."
                        required
                      />
                    </ColumnLayout>
                    <Grid gridDefinition={[{ colspan: 8 }, { colspan: 8 }]}>
                      <TextAreaFormField
                        label="Next Step"
                        stretch
                        name="opportunity.nextStep"
                        description="Defines the next steps to progress this opportunity"
                        required
                      />
                    </Grid>

                    <InputFormField
                      name="opportunity.externalLink"
                      inputMode="text"
                      label="External Link"
                      description="Enter a URL for any relevant content (i.e. Tech Business Plan)"
                    />
                  </SpaceBetween>
                </ExpandableSection>

                {!id && (
                  <Container header={<Header>Task (Activity)</Header>}>
                    <SpaceBetween size="l">
                      {tasks.map((_, index) => (
                        <Fieldset legend={`Task ${index + 1}`} key={`task-${index.toString()}`}>
                          <FieldContent opportunityData={oppFormValues} namePrefix={`tasks[${index}]`} hideRelatedPicker onInfoClicked={handleInfoClicked}>
                            <Button onClick={handleTaskDelete(index)} disabled={isSubmitting || isLoading}>
                              Delete
                            </Button>
                          </FieldContent>
                        </Fieldset>
                      ))}

                      <Button disabled={tasks.length > 2 || isSubmitting || isLoading} onClick={handleAddTaskClick} formAction="none">
                        Add Task
                      </Button>
                    </SpaceBetween>
                  </Container>
                )}
              </SpaceBetween>
            </Form>
          </form>
        )
      }}
    </Formik>
  )
}

export default FormContent
